// PrivateShowStatusTitle.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Trans from '../component/Trans.jsx';
import Animator from '../component/Animator.jsx';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import { privateShowStatus } from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import TextEllipsis from '../style/TextEllipsis.js';

export const PrivateShowStatusTitle = ({
  status = null,
  timestampOffsetSeconds = 0,
  fundingExp = 0,
  fundingTarget = 0,
  fundingProgress = 0,
  performNbf = 0,
  performExp = 0,
  shouldShowCountdownWhenPerforming = false,
}) => {
  const { t } = useTranslation();
  const getFormattedSeconds = ({ unixTimestamp }) => {
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    return formatTimeStringBySec(Math.max(0, unixTimestamp - now));
  };
  if (
    [
      privateShowStatus.FUNDING,
      privateShowStatus.FUNDING_FINAL_CALL,
      privateShowStatus.CONSIDERING,
    ].includes(status)
  ) {
    return (
      <StyledPrivateShowStatusTitle>
        <Trans
          defaults={t('popup_hint', {
            number: fundingTarget - fundingProgress,
            time: '__TIME__',
            ns: TranslationNamespace.LIVESTREAM,
          }).replace('__TIME__', '<0></0>')}
          components={[
            <Animator
              key={0}
              fps={1}
              render={() => getFormattedSeconds({ unixTimestamp: fundingExp })}
            ></Animator>,
          ]}
        />
      </StyledPrivateShowStatusTitle>
    );
  }
  if (status === privateShowStatus.REFUNDING) {
    return (
      <StyledPrivateShowStatusTitle>
        {t('return_diamond', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </StyledPrivateShowStatusTitle>
    );
  }
  if (status === privateShowStatus.PREPARING) {
    return (
      <StyledPrivateShowStatusTitle>
        <Trans
          defaults={t('show_countdown', {
            time: '__TIME__',
            ns: TranslationNamespace.LIVESTREAM,
          }).replace('__TIME__', '<0></0>')}
          components={[
            <Animator
              key={0}
              fps={1}
              render={() => getFormattedSeconds({ unixTimestamp: performNbf })}
            ></Animator>,
          ]}
        />
      </StyledPrivateShowStatusTitle>
    );
  }
  if (status === privateShowStatus.PERFORMING) {
    return (
      <StyledPrivateShowStatusTitle>
        {shouldShowCountdownWhenPerforming ? (
          <Trans
            defaults={t('show_end_countdown', {
              time: '__TIME__',
              ns: TranslationNamespace.LIVESTREAM,
            }).replace('__TIME__', '<0></0>')}
            components={[
              <Animator
                key={0}
                fps={1}
                render={() =>
                  getFormattedSeconds({
                    unixTimestamp: performExp,
                  })
                }
              ></Animator>,
            ]}
          />
        ) : (
          t('show_show_time', {
            ns: TranslationNamespace.LIVESTREAM,
          })
        )}
      </StyledPrivateShowStatusTitle>
    );
  }
  return null;
};

PrivateShowStatusTitle.propTypes = {
  status: PropTypes.string,
  timestampOffsetSeconds: PropTypes.number,
  fundingExp: PropTypes.number,
  fundingTarget: PropTypes.number,
  fundingProgress: PropTypes.number,
  performNbf: PropTypes.number,
  performExp: PropTypes.number,
  shouldShowCountdownWhenPerforming: PropTypes.bool,
};

const StyledPrivateShowStatusTitle = styled.div`
  ${TextEllipsis};
`;

export default PrivateShowStatusTitle;
